/**
 * Dates API (birthdays + one-off reminders)
 *
 * A private per-user list. Birthdays repeat every year and the year is optional
 * (it only shows an age). Reminders are a single calendar day: year required,
 * gone from the list once that day has passed, and the bell must not call them
 * a birthday.
 */

import supabase from "../config/supabase";

export const DATE_KIND_BIRTHDAY = "birthday";
export const DATE_KIND_REMINDER = "reminder";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const startOfToday = (from = new Date()) =>
  new Date(from.getFullYear(), from.getMonth(), from.getDate());

/**
 * The next time this month/day falls on or after `from`.
 *
 * 29 February only exists every fourth year; in the others we treat it as
 * 1 March rather than skipping the person for three years running. Mirrors
 * next_birthday_occurrence() in migration 021 so the list and the reminders
 * agree on which day a birthday lands.
 */
export function nextOccurrence(month, day, from = new Date()) {
  const startOfDay = startOfToday(from);

  for (let offset = 0; offset <= 1; offset += 1) {
    const year = startOfDay.getFullYear() + offset;
    let candidate = new Date(year, month - 1, day);

    // JS rolls invalid dates forward (29 Feb becomes 1 Mar), which is the
    // behaviour we want — but only trust it when the month actually shifted.
    if (candidate.getMonth() !== month - 1) {
      candidate = new Date(year, 2, 1);
    }

    if (candidate >= startOfDay) return candidate;
  }

  return new Date(startOfDay.getFullYear() + 1, month - 1, day);
}

/** Whole days from today until the next occurrence. 0 means today. */
export function daysUntil(month, day, from = new Date()) {
  const startOfDay = startOfToday(from);
  const next = nextOccurrence(month, day, from);
  return Math.round((next - startOfDay) / 86400000);
}

/** The actual calendar day of a one-off reminder, with the same 29 Feb rule. */
export function reminderDate(year, month, day) {
  let candidate = new Date(year, month - 1, day);
  if (candidate.getMonth() !== month - 1) {
    candidate = new Date(year, 2, 1);
  }
  return candidate;
}

export function daysUntilReminder(year, month, day, from = new Date()) {
  const startOfDay = startOfToday(from);
  const when = reminderDate(year, month, day);
  return Math.round((when - startOfDay) / 86400000);
}

/** "Today", "Tomorrow", "In 6 days", "In 3 weeks", "In 5 months". */
export function formatCountdown(days) {
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days < 14) return `In ${days} days`;
  if (days < 60) return `In ${Math.round(days / 7)} weeks`;
  return `In ${Math.round(days / 30)} months`;
}

/** "14 March" */
export function formatBirthDate(month, day) {
  return `${day} ${MONTHS[month - 1]}`;
}

/** "14 March 2026" */
export function formatReminderDate(month, day, year) {
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/**
 * Age they're turning at their next birthday, or null when the year is unknown.
 */
export function turningAge(row, from = new Date()) {
  if (!row.birth_year) return null;
  return nextOccurrence(row.birth_month, row.birth_day, from).getFullYear() - row.birth_year;
}

function isReminder(row) {
  return row.kind === DATE_KIND_REMINDER;
}

/** Decorates a row with the derived fields the list UI sorts and renders by. */
function decorate(row, from = new Date()) {
  if (isReminder(row)) {
    if (!row.birth_year) return null;
    const days = daysUntilReminder(row.birth_year, row.birth_month, row.birth_day, from);
    if (days < 0) return null;
    return {
      ...row,
      kind: DATE_KIND_REMINDER,
      daysUntil: days,
      countdown: formatCountdown(days),
      dateLabel: formatReminderDate(row.birth_month, row.birth_day, row.birth_year),
      turning: null,
    };
  }

  const days = daysUntil(row.birth_month, row.birth_day, from);
  return {
    ...row,
    kind: DATE_KIND_BIRTHDAY,
    daysUntil: days,
    countdown: formatCountdown(days),
    dateLabel: formatBirthDate(row.birth_month, row.birth_day),
    turning: turningAge(row, from),
  };
}

const birthdaysApi = {
  /**
   * Everyone on the list, soonest first — the top of the list is always the one
   * that matters, which is not the same as calendar order. Past reminders are
   * omitted rather than wrapping into next year.
   */
  async list() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, data: [] };

    const { data, error } = await supabase
      .from("birthdays")
      .select("*")
      .eq("user_id", user.id);

    if (error) return { success: false, error: error.message, data: [] };

    const now = new Date();
    const decorated = (data || [])
      .map((row) => decorate(row, now))
      .filter(Boolean)
      .sort((a, b) => a.daysUntil - b.daysUntil || a.person_name.localeCompare(b.person_name));

    return { success: true, data: decorated };
  },

  async create({ personName, month, day, year = null, notes = null, kind = DATE_KIND_BIRTHDAY }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not signed in" };

    const { data, error } = await supabase
      .from("birthdays")
      .insert({
        user_id: user.id,
        person_name: personName.trim(),
        birth_month: month,
        birth_day: day,
        birth_year: year,
        notes,
        kind,
      })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: decorate(data) };
  },

  async update(id, { personName, month, day, year = null, notes = null, kind = DATE_KIND_BIRTHDAY }) {
    const { data, error } = await supabase
      .from("birthdays")
      .update({
        person_name: personName.trim(),
        birth_month: month,
        birth_day: day,
        birth_year: year,
        notes,
        kind,
      })
      .eq("id", id)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: decorate(data) };
  },

  async remove(id) {
    const { error } = await supabase.from("birthdays").delete().eq("id", id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  getMonths() {
    return MONTHS;
  },
};

export default birthdaysApi;
